import React, { useState, useRef, useEffect } from 'react';
import DownArrow from '@/assets/Icons/components/date-picker/DownArrow';
import ChevronRight from '@/assets/Icons/components/date-picker/ChevronRight';
import ChevronLeft from '@/assets/Icons/components/date-picker/ChevronLeft';
import { CustomDatePickerProps } from './types/CustomInputsTypes';
import { formatToUSCentralTime, getCurrentUSCentralTime } from './USCentralTimeUtils';
import { Calendar } from 'lucide-react';
import { Icon } from '@mui/material';

const CustomDateInput: React.FC<CustomDatePickerProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder = 'Select Date',
  required = false,
  description,
  width = 'w-full',
  isEditable = true,
  className = '',
  dateFormat = 'dd-mm-yyyy',
  showRunningTime = false, // Prop to toggle running time display
  onFocus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(null);
  const [currentTime, setCurrentTime] = useState<string>('');
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Format function for date display (for non-running time display)
  const formatDate = (date: Date | null): string => {
    if (!date) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();

    switch (dateFormat.toLowerCase()) {
      case 'dd-mm-yyyy':
        return `${day}-${month}-${year}`;
      case 'mm-dd-yyyy':
        return `${month}-${day}-${year}`;
      case 'yyyy-mm-dd':
        return `${year}-${month}-${day}`;
      case 'mm/dd/yy':
        return `${month}/${day}/${year}`;
      default:
        return `${day}-${month}-${year}`;
    }
  };

  // Update running time every second
  useEffect(() => {
    if (showRunningTime) {
      const timer = setInterval(() => {
        // Get current US Central Time
        const newTime = getCurrentUSCentralTime();
        setCurrentTime(newTime);

        // If date is already set and using running time format, update it
        if (value && value.includes(',') && (value.includes('CST') || value.includes('CDT'))) {
          onChange({
            target: {
              name,
              value: newTime,
              type: 'date',
            },
          });
        }
      }, 1000);

      // Initialize time immediately
      const initialTime = getCurrentUSCentralTime();
      setCurrentTime(initialTime);

      // Initialize value if it's empty and running time is enabled
      if (!value && showRunningTime) {
        onChange({
          target: {
            name,
            value: initialTime,
            type: 'date',
          },
        });
      }

      return () => clearInterval(timer);
    }
  }, [showRunningTime, value, name, onChange]);

  // Parse date from string
  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;

    // Check if we're dealing with a running time format
    if (dateString.includes(',') && (dateString.includes('CST') || dateString.includes('CDT'))) {
      // Extract just the date part (before the comma)
      dateString = dateString.split(',')[0];

      // MM/DD/YY format expected
      const parts = dateString.split('/');
      if (parts.length !== 3) return null;

      const month = parseInt(parts[0], 10) - 1; // JS months are 0-indexed
      const day = parseInt(parts[1], 10);
      const year = parseInt('20' + parts[2], 10); // Add '20' prefix to convert 'YY' to 'YYYY'

      const date = new Date(year, month, day);
      return date;
    }

    // Original parsing logic
    let day, month, year;
    const parts = dateString.split('-');

    if (parts.length !== 3) return null;

    switch (dateFormat.toLowerCase()) {
      case 'dd-mm-yyyy':
        [day, month, year] = parts;
        break;
      case 'mm-dd-yyyy':
        [month, day, year] = parts;
        break;
      case 'yyyy-mm-dd':
        [year, month, day] = parts;
        break;
      default:
        [day, month, year] = parts;
    }

    // Parse to integers
    const iYear = parseInt(year, 10);
    const iMonth = parseInt(month, 10) - 1; // JS months are 0-indexed
    const iDay = parseInt(day, 10);

    // Validate date
    if (isNaN(iYear) || isNaN(iMonth) || isNaN(iDay)) return null;
    if (iMonth < 0 || iMonth > 11) return null;

    const date = new Date(iYear, iMonth, iDay);

    // Check if the date is valid
    if (date.getFullYear() !== iYear || date.getMonth() !== iMonth || date.getDate() !== iDay) {
      return null;
    }

    return date;
  };

  // Initialize the selected date from props
  useEffect(() => {
    if (value) {
      const parsedDate = parseDate(value);
      if (parsedDate) {
        setSelectedDate(parsedDate);
        setTempSelectedDate(parsedDate);
        setCurrentMonth(parsedDate);
      }
    }
  }, [value]);

  // Close datepicker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Reset temp selection to actual selection
        setTempSelectedDate(selectedDate);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedDate]);

  // Month navigation
  const prevMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Get current month days and surrounding days
  const getMonthDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    const daysInMonth = lastDay.getDate();
    let dayOfWeek = firstDay.getDay();
    // Convert Sunday (0) to 7 to make Monday the first day
    dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    // Array to hold all the days we want to display
    const days = [];

    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = dayOfWeek - 1; i > 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i + 1),
        isCurrentMonth: false,
      });
    }

    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    // Add days from next month
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          date: new Date(year, month + 1, i),
          isCurrentMonth: false,
        });
      }
    }

    return days;
  };

  // Month names
  const monthNames = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];

  // Handle date selection - Now only sets temporary selection
  const handleDateSelect = (date: Date) => {
    setTempSelectedDate(date);
    // No longer closing dropdown or applying the change immediately
  };

  // Handle apply button
  const handleApply = () => {
    if (tempSelectedDate) {
      // Apply the temporary selection to the actual selection
      setSelectedDate(tempSelectedDate);

      let formattedValue;
      if (showRunningTime) {
        // Use our utility function to get US Central Time with the selected date
        const now = new Date();
        // Create a date with the selected date and current time
        const selectedDateTime = new Date(
          tempSelectedDate.getFullYear(),
          tempSelectedDate.getMonth(),
          tempSelectedDate.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds()
        );

        formattedValue = formatToUSCentralTime(selectedDateTime);
      } else {
        formattedValue = formatDate(tempSelectedDate);
      }

      onChange({
        target: {
          name,
          value: formattedValue,
          type: 'date',
        },
      });
    }
    setIsOpen(false);
  };

  // Handle cancel button
  const handleCancel = () => {
    // Reset temp selection to actual selection
    setTempSelectedDate(selectedDate);
    setIsOpen(false);
  };

  // Check if a date is selected (now using tempSelectedDate for display)
  const isDateSelected = (date: Date) => {
    if (!tempSelectedDate) return false;
    return (
      date.getDate() === tempSelectedDate.getDate() &&
      date.getMonth() === tempSelectedDate.getMonth() &&
      date.getFullYear() === tempSelectedDate.getFullYear()
    );
  };

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const days = getMonthDays();

  return (
    <div className={`flex flex-col p-1 ${width} ${className}`} ref={datePickerRef}>
      {label && (
        <label className={`flex mt-4 ${className ? className : 'text-ds-gray-800'} font-semibold`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {description && <p className="text-ds-gray-300 text-xs mt-1">{description}</p>}

      <div className="relative py-2 submission">
        <div
          className={`border-2 h-[40px] ${width ? width : 'w-[400px]'} rounded-md py-2 px-3 flex justify-between items-center cursor-pointer
            ${isEditable ? 'hover:border-blue-1700' : 'bg-[#F7F7F7] cursor-not-allowed'} 
            ${isOpen ? 'border-blue-1700' : 'border-blue-2100'}`}
          onClick={() => {
            if (isEditable) {
              setIsOpen(!isOpen);
              onFocus?.();
            }
          }}
        >
          <span className={`${!value ? 'text-ds-gray-300 text-xs' : 'text-ds-gray-700 text-xs'}`}>
            {showRunningTime && !value ? currentTime : value || placeholder}
          </span>
          <Icon
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-ds-gray-500"
            component={Calendar}
          />
        </div>

        {isOpen && isEditable && (
          <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-lg overflow-hidden">
            {/* Calendar header */}
            <div className="flex justify-between items-center p-1 border-b border-red-50">
              <button
                type="button"
                className="p-1 hover:bg-purple-50 rounded-full"
                onClick={prevMonth}
              >
                <ChevronLeft />
              </button>

              <div className="flex gap-2">
                <div className="relative">
                  <select
                    value={currentMonth.getFullYear()}
                    onChange={(e) => {
                      const newDate = new Date(currentMonth);
                      newDate.setFullYear(parseInt(e.target.value));
                      setCurrentMonth(newDate);
                    }}
                    className="appearance-none border border-ds-gray-400 rounded py-1 focus:outline-none focus:ring-0 text-ds-gray-700 text-xs px-3 pr-8"
                  >
                    {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - 10 + i).map(
                      (year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      )
                    )}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                    <DownArrow />
                  </div>
                </div>

                <div className="relative">
                  <select
                    value={currentMonth.getMonth()}
                    onChange={(e) => {
                      const newDate = new Date(currentMonth);
                      newDate.setMonth(parseInt(e.target.value));
                      setCurrentMonth(newDate);
                    }}
                    className="appearance-none border border-ds-gray-400 text-ds-gray-700 text-xs rounded py-1 px-3 pr-8 focus:outline-none focus:ring-0"
                  >
                    {monthNames.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                    <DownArrow />
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="p-1 hover:bg-purple-50 rounded-full"
                onClick={nextMonth}
              >
                <ChevronRight />
              </button>
            </div>

            {/* Calendar body */}
            <div className="p-2">
              {/* Days of week */}
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-ds-gray-700 mb-1">
                <div>Mon</div>
                <div>Tue</div>
                <div>Wed</div>
                <div>Thu</div>
                <div>Fri</div>
                <div>Sat</div>
                <div>Sun</div>
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`w-6 h-6 flex items-center justify-center text-sm rounded-sm transition-all
                      ${day.isCurrentMonth ? 'hover:border hover:border-ds-brand-mc-blue-1' : 'text-gray-2400 hover:bg-gray-100'}
                      ${isDateSelected(day.date) ? 'bg-ds-brand-mc-blue-1 text-white hover:bg-blue-700 hover:text-gray-2400' : ''}
                      ${isToday(day.date) && !isDateSelected(day.date) ? 'border border-blue-500' : ''}`}
                    onClick={() => handleDateSelect(day.date)}
                  >
                    {day.date.getDate()}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-2 flex justify-between items-center border-t border-red-50">
              <div className="text-sm text-ds-gray-800">
                {showRunningTime ? 'MM/DD/YYYY, HH:MM AM/PM CST/CDT' : dateFormat.toUpperCase()}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1 border text-ds-gray-500 text-sm hover:bg-gray-100 rounded"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-sm bg-ds-brand-mc-blue-1 text-white rounded"
                  onClick={handleApply}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDateInput;
